// Using our own Node type for now - will be replaced with unist when dependencies are added
export type Node = {
  type: string;
  children?: Node[];
  value?: string;
  data?: Record<string, unknown>;
  position?: {
    start: { line: number; column: number; offset: number };
    end: { line: number; column: number; offset: number };
  };
};

/**
 * Heading node with proper typing for TOC generation
 */
export interface HeadingNode extends Node {
  type: 'heading';
  depth: number;
  data: {
    hProperties: {
      id: string;
    };
  };
  children: Node[];
}

/**
 * Type guard to check if a node is a valid heading node
 */
export function isHeadingNode(node: Node): node is HeadingNode {
  return (
    node.type === 'heading' &&
    'depth' in node &&
    typeof (node as any).depth === 'number' &&
    node.data != null &&
    typeof node.data === 'object' &&
    'hProperties' in node.data &&
    node.data.hProperties != null &&
    typeof node.data.hProperties === 'object' &&
    'id' in node.data.hProperties &&
    typeof (node.data.hProperties as Record<string, any>).id === 'string'
  );
}

/**
 * Extended Node type with frontmatter support
 * Used throughout the pipeline to maintain frontmatter metadata
 */
export interface ASTWithFrontmatter extends Node {
  frontmatter?: FrontmatterData;
}

/**
 * Frontmatter data extracted from Markdown documents
 * Supports YAML and TOML formats
 */
export interface FrontmatterData {
  title?: string;
  author?: string;
  date?: string;
  keywords?: string[];
  subject?: string;
  language?: string;
  [key: string]: unknown; // Allow additional custom fields
}

export interface Configuration {
  version: string;
  document: DocumentConfig;
  page: PageConfig;
  features: FeaturesConfig;
  theme: string;
  customCSS: string[];
  fonts: FontConfig[];
  header: TemplateConfig;
  footer: TemplateConfig;
  pdf: PDFConfig;
  images: ImageConfig;
  processing: ProcessingConfig;
  output: OutputConfig;
  plugins: PluginConfig[];
  dev: DevConfig;
  validation: ValidationConfig;
  logging: LoggingConfig;
}

export interface DocumentConfig {
  title: string;
  author: string;
  date: string;
  language: string;
  keywords: string[];
  subject: string;
}

export interface PageConfig {
  format: string;
  orientation: 'portrait' | 'landscape';
  margins: {
    top: string;
    right: string;
    bottom: string;
    left: string;
  };
}

export interface FeaturesConfig {
  toc: TOCConfig;
  numbering: NumberingConfig;
  crossReferences: CrossReferencesConfig;
  math: MathConfig;
  diagrams: DiagramsConfig;
  codeHighlight: CodeHighlightConfig;
  callouts: CalloutsConfig;
  citations: CitationsConfig;
}

export interface TOCConfig {
  enabled: boolean;
  depth: number;
  title: string;
  position: string;
}

export interface NumberingConfig {
  headings: boolean;
  figures: boolean;
  tables: boolean;
  equations: boolean;
}

export interface CrossReferencesConfig {
  enabled: boolean;
  prefix: {
    figure: string;
    table: string;
    section: string;
  };
}

export interface MathConfig {
  enabled: boolean;
  renderer: 'katex' | 'mathjax';
  macros: Record<string, string>;
}

export interface DiagramsConfig {
  mermaid: boolean;
  plantuml: boolean;
}

export interface CodeHighlightConfig {
  enabled: boolean;
  theme: string;
  showLineNumbers: boolean;
  languages: 'auto' | string[];
}

export interface CalloutsConfig {
  enabled: boolean;
  types: string[];
}

export interface CitationsConfig {
  enabled: boolean;
  style: string;
  bibliography: string;
}

export interface FontConfig {
  family: string;
  files: Array<{
    path: string;
    weight: number;
    style: string;
  }>;
}

export interface TemplateConfig {
  enabled: boolean;
  template: string;
  height: string;
}

export interface PDFConfig {
  version: string;
  embedFonts: boolean;
  compression: boolean;
  pdfA: boolean;
  pdfUA: boolean;
  tagged: boolean;
  encryption: EncryptionConfig;
}

export interface EncryptionConfig {
  enabled: boolean;
  userPassword: string;
  ownerPassword: string;
  permissions: {
    print: boolean;
    copy: boolean;
    modify: boolean;
  };
}

export interface ImageConfig {
  optimize: boolean;
  quality: number;
  maxWidth: number;
  embedRemote: boolean;
  cache: boolean;
  cacheDir: string;
}

export interface ProcessingConfig {
  parallel: boolean;
  maxWorkers: number;
  timeout: number;
}

export interface OutputConfig {
  directory: string;
  filename: string;
  overwrite: boolean;
}

export interface PluginConfig {
  name: string;
  enabled: boolean;
  options?: Record<string, unknown>;
}

export interface DevConfig {
  watch: boolean;
  liveReload: boolean;
  port: number;
  open: boolean;
}

export interface ValidationConfig {
  markdown: {
    enabled: boolean;
    rules: string;
  };
  links: {
    checkExternal: boolean;
    checkInternal: boolean;
  };
  accessibility: {
    enabled: boolean;
    level: 'A' | 'AA' | 'AAA';
  };
}

export interface LoggingConfig {
  level: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  file: string;
  format: 'text' | 'json';
}

export interface PipelineResult {
  pdf: Buffer;
  html?: string;
  metadata: PDFMetadata;
  stats: ProcessingStats;
  warnings: string[];
}

export interface PDFMetadata {
  title: string;
  author: string;
  subject: string;
  keywords: string[];
  creator: string;
  producer: string;
  creationDate: Date;
}

export interface ProcessingStats {
  duration: number;
  pageCount: number;
  wordCount: number;
  imageCount: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface Theme {
  name: string;
  metadata: ThemeMetadata;
  styles: string[];
  templates: Record<string, string>;
}

export interface ThemeMetadata {
  name: string;
  version: string;
  description: string;
  author: string;
}

export interface CommandDefinition {
  name: string;
  description: string;
  handler: (args: Record<string, unknown>) => Promise<void>;
}

// Plugin Interface
export interface MDExportPlugin {
  name: string;
  version: string;
  description?: string;
  author?: string;

  hooks?: {
    beforeParse?: (content: string, config: Configuration) => string | Promise<string>;
    afterParse?: (ast: Node, config: Configuration) => Node | Promise<Node>;
    beforeRender?: (html: string, config: Configuration) => string | Promise<string>;
    afterRender?: (html: string, config: Configuration) => string | Promise<string>;
    beforePDF?: (html: string, config: Configuration) => string | Promise<string>;
    afterPDF?: (pdf: Buffer, config: Configuration) => Buffer | Promise<Buffer>;
  };

  transformers?: {
    remark?: unknown[];
    rehype?: unknown[];
  };

  themes?: Theme[];
  commands?: CommandDefinition[];
  configSchema?: unknown;
}

// Error Types
export class MDExportError extends Error {
  constructor(message: string, options?: { cause?: Error }) {
    super(message);
    this.name = 'MDExportError';
    this.cause = options?.cause;
  }
}

export class ParseError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'ParseError';
  }
}

export class RenderError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'RenderError';
  }
}

export class GenerationError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'GenerationError';
  }
}

export class ProcessingError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'ProcessingError';
  }
}

export class PluginLoadError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'PluginLoadError';
  }
}

export class ConfigValidationError extends MDExportError {
  errors: unknown[];

  constructor(message: string, options: { errors: unknown[] }) {
    super(message);
    this.name = 'ConfigValidationError';
    this.errors = options.errors;
  }
}

export class ThemeError extends MDExportError {
  constructor(message: string, options?: { cause?: Error }) {
    super(message, options);
    this.name = 'ThemeError';
  }
}
